<template>
  <div class="auth-container">
    <van-nav-bar title="人脸识别认证" class="auth-navbar" />

    <div class="auth-content">
      <div class="auth-header">
        <p class="auth-desc">请确保光线充足，面部无遮挡，正对摄像头</p>
      </div>

      <div class="camera-preview-area">
        <video
          ref="cameraVideoRef"
          autoplay
          playsinline
          class="camera-video"
        ></video>
        <canvas ref="cameraCanvasRef" style="display: none"></canvas>

        <!-- 拍摄预览 -->
        <div v-if="capturedPhoto" class="captured-preview">
          <img :src="capturedPhoto" alt="拍摄预览" class="preview-image" />
        </div>

        <!-- 错误提示 -->
        <div v-if="cameraError" class="camera-error">
          <p>{{ cameraError }}</p>
          <button @click="retryCamera" class="retry-btn">重试</button>
        </div>

        <!-- 验证中遮罩 -->
        <div v-if="isVerifying" class="verifying-overlay">
          <div class="loading-spinner"></div>
          <p>验证中...</p>
        </div>
      </div>
    </div>

    <div class="camera-controls">
      <button
        v-if="!capturedPhoto"
        class="capture-btn"
        @click="takePhoto"
        :disabled="!cameraReady"
      >
        📷 拍摄
      </button>
      <button v-if="capturedPhoto" class="confirm-btn" @click="confirmPhoto">
        ✓ 确认
      </button>
      <button v-if="capturedPhoto" class="retake-btn" @click="retakePhoto">
        ↺ 重拍
      </button>
    </div>
  </div>
</template>

<script>
import { Toast } from 'vant'

export default {
  name: 'authCamera',
  data() {
    return {
      mediaStream: null,
      capturedPhoto: null,
      cameraReady: false,
      cameraError: null,
      isVerifying: false,
      // API 地址（相对 /api/）
      targetUrl: 'realauth/pictureCompare',
      // 返回地址（相对路径）
      returnUrl: '/detailsList',
      // 回传给原页面的额外参数
      otherParam: '',
      // 用户ID
      uid: 0
    }
  },
  mounted() {
    // 禁止页面滚动
    document.body.style.overflow = 'hidden'
    // 解析路由参数
    this.parseRouteParams()
    // 打开摄像头
    this.openCamera()
  },
  beforeDestroy() {
    // 恢复页面滚动
    document.body.style.overflow = ''
    this.stopCameraStream()
  },
  methods: {
    /**
     * 解析路由参数，确定调用哪个API以及返回地址
     */
    parseRouteParams() {
      const routeQuery = this.$route.query
      let paramStr = ''
      // 兼容 url query 与 location search
      const urlParams = new URLSearchParams(window.location.search)
      const merged = {}
      for (const [key, value] of urlParams) {
        merged[key] = value
      }
      for (const key in routeQuery) {
        if (routeQuery.hasOwnProperty(key)) {
          merged[key] = routeQuery[key]
        }
      }
      for (const key in merged) {
        const value = merged[key]
        if (key === 'uid') {
          this.uid = value
          continue
        }
        if (key === 'page') {
          if (value === 'study') {
            this.targetUrl = 'realauth/pictureCompare'
            this.returnUrl = '/detailsList'
          } else {
            this.targetUrl = 'realauth/examPictureCompare'
            this.returnUrl = '/detailsList'
          }
        }
        paramStr += key + '=' + value + '&'
      }
      this.otherParam = paramStr.substring(0, paramStr.length - 1)
    },

    /**
     * 打开摄像头
     */
    async openCamera() {
      // 检查HTTPS环境
      if (
        location.protocol !== 'https:' &&
        location.hostname !== 'localhost' &&
        location.hostname !== '127.0.0.1'
      ) {
        this.cameraError = '摄像头功能需要在 HTTPS 环境下使用'
        return
      }

      // 检查浏览器支持
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.cameraError = '您的浏览器不支持摄像头功能'
        return
      }

      try {
        const constraints = {
          video: {
            width: { ideal: 720 },
            height: { ideal: 1280 },
            facingMode: 'user'
          },
          audio: false
        }

        const stream = await navigator.mediaDevices.getUserMedia(constraints)
        this.mediaStream = stream

        const videoElement = this.$refs.cameraVideoRef
        if (videoElement) {
          videoElement.srcObject = stream

          await new Promise((resolve, reject) => {
            if (!videoElement) {
              reject(new Error('Video element not found'))
              return
            }
            videoElement.onloadedmetadata = () => {
              videoElement
                .play()
                .then(resolve)
                .catch(reject)
            }
            videoElement.onerror = reject
          })

          this.cameraReady = true
          this.cameraError = null
        }
      } catch (error) {
        console.error('启动摄像头失败:', error)
        this.cameraReady = false

        if (error.name === 'NotAllowedError') {
          this.cameraError = '请授权摄像头权限后重试'
        } else if (error.name === 'NotFoundError') {
          this.cameraError = '未检测到摄像头设备'
        } else {
          this.cameraError =
            '无法打开摄像头：' + (error.message || '请检查权限设置')
        }
      }
    },

    /**
     * 重试打开摄像头
     */
    retryCamera() {
      this.cameraError = null
      this.cameraReady = false
      this.capturedPhoto = null
      this.stopCameraStream()
      setTimeout(() => {
        this.openCamera()
      }, 500)
    },

    /**
     * 拍摄照片
     */
    takePhoto() {
      const videoElement = this.$refs.cameraVideoRef
      const canvasElement = this.$refs.cameraCanvasRef

      if (!videoElement || !canvasElement) {
        Toast.fail('无法获取视频画面')
        return
      }

      if (videoElement.readyState !== 4 || videoElement.videoWidth === 0) {
        Toast.fail('视频未就绪，请稍后重试')
        return
      }

      canvasElement.width = videoElement.videoWidth
      canvasElement.height = videoElement.videoHeight

      const context = canvasElement.getContext('2d')
      if (!context) {
        Toast.fail('无法创建画布上下文')
        return
      }

      context.drawImage(
        videoElement,
        0,
        0,
        canvasElement.width,
        canvasElement.height
      )
      this.capturedPhoto = canvasElement.toDataURL('image/jpeg', 0.8)
      Toast.success('拍照成功')

      // 停止摄像头流
      if (this.mediaStream) {
        this.mediaStream.getVideoTracks().forEach(track => track.stop())
        this.mediaStream = null
        this.cameraReady = false
      }
    },

    /**
     * 照片比对认证
     */
    async pictureDeal() {
      if (!this.capturedPhoto) {
        Toast('请先拍照')
        return
      }

      this.isVerifying = true

      try {
        const param = {
          base64: this.capturedPhoto,
          uid: this.uid,
          token: JSON.parse(localStorage.getItem('jin-token')) || ''
        }
        const { data } = await this.postFormRequest(
          'api/' + this.targetUrl,
          param
        )

        if (data.code == 1 && data.data.passed === 'T') {
          Toast.success('认证成功')
          this.goBack(true)
        } else {
          Toast.fail('认证失败，请重新拍摄')
          this.goBack(false)
        }
      } catch (error) {
        console.error('认证失败:', error)
        Toast.fail((error && error.message) || '认证失败，请重试')
        this.capturedPhoto = null
        this.openCamera()
      } finally {
        this.isVerifying = false
      }
    },

    /**
     * 确认照片
     */
    async confirmPhoto() {
      if (!this.capturedPhoto) {
        Toast('请先拍照')
        return
      }
      await this.pictureDeal()
    },

    /**
     * 重拍
     */
    retakePhoto() {
      this.capturedPhoto = null
      this.openCamera()
    },

    /**
     * 停止摄像头
     */
    stopCameraStream() {
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach(track => {
          track.stop()
        })
        this.mediaStream = null
      }
      const videoElement = this.$refs.cameraVideoRef
      if (videoElement) {
        videoElement.srcObject = null
      }
      this.cameraReady = false
    },

    /**
     * 返回原页面（使用 window.location.href 触发完整刷新以重新校验认证状态）
     * @param {boolean} success 是否认证成功
     */
    goBack(success) {
      let url = this.returnUrl + '?picture=' + (success ? '1' : '0')
      if (this.otherParam) {
        url += '&' + this.otherParam
      }
      window.location.href = url
    }
  }
}
</script>

<style scoped lang="less">
.auth-container {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.auth-navbar {
  background-color: transparent !important;
}

.auth-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 0 16px;
  box-sizing: border-box;
}

.auth-header {
  text-align: center;
  padding: 16px 0;
  color: #fff;

  .auth-desc {
    margin: 0;
    font-size: 14px;
    opacity: 0.9;
    line-height: 1.5;
  }
}

.camera-preview-area {
  position: relative;
  background-color: #000;
  flex: 1;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 360px;
  margin-bottom: 16px;
}

.camera-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.captured-preview {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.camera-error {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  text-align: center;
  padding: 20px;
  box-sizing: border-box;

  p {
    font-size: 14px;
    line-height: 1.6;
  }
}

.retry-btn {
  padding: 10px 28px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 24px;
  font-size: 14px;
  margin-top: 16px;
}

.verifying-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  z-index: 10;

  p {
    margin-top: 10px;
    font-size: 14px;
  }
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.camera-controls {
  display: flex;
  justify-content: center;
  gap: 12px;
  padding: 16px;
  box-sizing: border-box;
}

.capture-btn,
.confirm-btn,
.retake-btn {
  flex: 1;
  max-width: 160px;
  padding: 14px 0;
  border: none;
  border-radius: 30px;
  font-size: 16px;
  font-weight: 500;
}

.capture-btn:disabled {
  background-color: #ccc;
  color: #fff;
}

.capture-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.confirm-btn {
  background-color: #52c41a;
  color: white;
}

.retake-btn {
  background-color: #ff7875;
  color: white;
}
</style>
